import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Link from "@/components/Link";
import TimeAgo from "@/components/TimeAgo";
import { Alert as AlertType } from "@/components/proptypes";

import Form from "antd/lib/form";
import Button from "antd/lib/button";
import Tooltip from "@/components/Tooltip";
import AntAlert from "antd/lib/alert";
import * as Grid from "antd/lib/grid";

import Title from "./Title";
import Criteria from "./Criteria";
import Rearm from "./Rearm";
import Query from "./Query";
import AlertDestinations from "./AlertDestinations";
import HorizontalFormItem from "../HorizontalFormItem";
import { STATE_CLASS } from "../../pages/alerts/AlertsList";
import DynamicComponent from "@/components/DynamicComponent";

import "./AlertView.less";

function AlertState({ state, lastTriggered }) {
  return (
    <div className="alert-state">
      <span className={`alert-state-indicator label ${STATE_CLASS[state]}`}>Status: {state}</span>
      {state === "unknown" && <div className="ant-form-item-explain">Alert condition has not been evaluated.</div>}
      {lastTriggered && (
        <div className="ant-form-item-explain">
          Last triggered{" "}
          <span className="alert-last-triggered">
            <TimeAgo date={lastTriggered} />
          </span>
        </div>
      )}
    </div>
  );
}

AlertState.propTypes = {
  state: PropTypes.string.isRequired,
  lastTriggered: PropTypes.string,
};

AlertState.defaultProps = {
  lastTriggered: null,
};

// eslint-disable-next-line react/prefer-stateless-function
export default class AlertView extends React.Component {
  state = {
    unmuting: false,
    favoriting: false,
    archiving: false,
  };

  archive = () => {
    this.setState({ archiving: true });
    this.props.archive().finally(() => {
      this.setState({ archiving: false });
    }
    );
  }

  favorite = () => {
    this.setState({ favoriting: true });
    this.props.favorite().finally(() => {
      this.setState({ favoriting: false });
    }
    );
  }

  unarchive = () => {
    this.setState({ archiving: true });
    this.props.unarchive().finally(() => {
      this.setState({ archiving: false });
    }
    );
  }

  unfavorite = () => {
    this.setState({ favoriting: true });
    this.props.unfavorite().finally(() => {
      this.setState({ favoriting: false });
    }
    );
  }

  unmute = () => {
    this.setState({ unmuting: true });
    this.props.unmute().finally(() => {
      this.setState({ unmuting: false });
    });
  };

  render() {
    const { alert, queryResult, canEdit, onEdit, menuButton, onChange, setAlertTags } = this.props;
    const { query, name, options, rearm, } = alert;

    return (
      <>
        <Title name={name} alert={alert}  editMode={false} tagsExtra={null} onChange={onChange} canEdit={canEdit} setAlertTags={setAlertTags}>
          <DynamicComponent name="AlertView.HeaderExtra" alert={alert} />
          <Tooltip title={canEdit ? "" : "You do not have sufficient permissions to edit this alert"}>
            <Button type="default" onClick={canEdit ? onEdit : null} className={cx({ disabled: !canEdit })}>
              <i className="fa fa-edit m-r-5" aria-hidden="true" />
              Edit
            </Button>
            {menuButton}
          </Tooltip>
        </Title>
        <h3>Alert Settings</h3>
        <div className="bg-white tiled p-20">
          <Grid.Row type="flex" gutter={16}>
            <Grid.Col xs={24} md={16} className="d-flex">
              <Form className="flex-fill">
                <HorizontalFormItem>
                  <AlertState state={alert.state} lastTriggered={alert.last_triggered_at} />
                </HorizontalFormItem>
                <HorizontalFormItem label="Query">
                  <Query query={query} queryResult={queryResult} />
                </HorizontalFormItem>
                {queryResult && options && (
                  <>
                    <HorizontalFormItem label="Trigger when" className="alert-criteria">
                      <Criteria
                        columnNames={queryResult.getColumnNames()}
                        resultValues={queryResult.getData()}
                        alertOptions={options}
                      />
                    </HorizontalFormItem>
                    <HorizontalFormItem label="Notifications" className="form-item-line-height-normal">
                      <Rearm value={rearm || 0} />
                      <br />
                      Set to {options.custom_subject || options.custom_body ? "custom" : "default"} notification
                      template.
                    </HorizontalFormItem>
                  </>
                )}
              </Form>
            </Grid.Col>
            <Grid.Col xs={24} md={8}>
              {options.muted && (
                <AntAlert
                  className="m-b-20"
                  message={
                    <>
                      <i className="fa fa-bell-slash-o" aria-hidden="true" /> Notifications are muted
                    </>
                  }
                  description={
                    <>
                      Notifications for this alert will not be sent.
                      <br />
                      {canEdit && (
                        <>
                          To restore notifications click
                          <Button
                            size="small"
                            type="primary"
                            onClick={this.unmute}
                            loading={this.state.unmuting}
                            className="m-t-5 m-l-5">
                            Unmute
                          </Button>
                        </>
                      )}
                    </>
                  }
                  type="warning"
                />
              )}
              <h4>
                Destinations{" "}
                <Tooltip title="Open Alert Destinations page in a new tab.">
                  <Link href="destinations" target="_blank">
                    <i className="fa fa-external-link f-13" aria-hidden="true" />
                    <span className="sr-only">(opens in a new tab)</span>
                  </Link>
                </Tooltip>
              </h4>
              <AlertDestinations alertId={alert.id} />
            </Grid.Col>
          </Grid.Row>
        </div>
      </>
    );
  }
}

AlertView.propTypes = {
  alert: AlertType.isRequired,
  queryResult: PropTypes.object, // eslint-disable-line react/forbid-prop-types,
  canEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  menuButton: PropTypes.node.isRequired,
  unmute: PropTypes.func,
  favorite: PropTypes.func.isRequired,
  unfavorite: PropTypes.func.isRequired,
  archive: PropTypes.func.isRequired,
  unarchive: PropTypes.func.isRequired,
};

AlertView.defaultProps = {
  queryResult: null,
  unmute: null,
  
};
